import { useSockets, useNotificationStore, getI18n, CMSNotification, deserializeDatesInObject } from "@editor/ui-utils";
import { reactive, watch } from "vue";
const de = {
  locks: {
    lockedBy: "Das Formular ist gesperrt von:",
    break: "Entsperren",
    lockedByYou: "Das Formular ist von dir selbst in einem anderen Tab gesperrt",
    lockRemoved: "Sperre wurde entfernt",
    contentChanged: "Das Dokument wurde extern verändert. Dein Dokument kann in diesem Tab nicht mehr gespeichert werden.",
    refresh: "Aktualisieren",
    lockBroken: "Sie können dieses Dokument jetzt bearbeiten"
  }
};
const en = {
  locks: {
    lockedBy: "Form is locked by:",
    break: "Break lock",
    lockedByYou: "The form is locked by yourself in another tab",
    lockRemoved: "Lock was removed",
    contentChanged: "The document was updated externally. Your document can't be saved in this tab anymore.",
    refresh: "Refresh",
    lockBroken: "You can now edit this document"
  }
};
const messages = { en, de };
const getResourceChannelName = (resourceName) => `/locking/${resourceName.replaceAll("/", "-")}`;
const DefaultSingleResourceLockingTranslationKeys = {
  notificationLockGranted: "locks.lockBroken",
  notificationButtonBreakLock: "locks.break",
  notificationLockedYourself: "locks.lockedByYou",
  notificationLockedOthers: "locks.lockedBy"
};
const useSingleResourceLocking = (resourceType, isResourceChanged, translations = DefaultSingleResourceLockingTranslationKeys, isBreakLockAllowed = true, lockChangeCallback) => {
  const manager = useSockets();
  const socket = manager.socket(
    getResourceChannelName(resourceType)
  );
  let documentUpdateCallbacks = [];
  const state = reactive({
    initialized: false,
    resourceUuid: "",
    async init(resourceUuid) {
      state.resourceUuid = resourceUuid;
      state.initialized = true;
      socket.emit("watch", [state.resourceUuid]);
    },
    /**
     * Removes the lock and stops watching the resource
     * Sets the resourceUuid to empty string!
     * Call when cleaning up, ideally in onBeforeUnmount
     */
    teardown() {
      socket.emit("releaseLock", state.resourceUuid);
      socket.emit("unwatch", [state.resourceUuid]);
      state.resourceUuid = "";
      documentUpdateCallbacks = [];
    },
    /**
     * Adds a lock on the resource
     * @param breakExisting If true, will break existing lock if it exists
     */
    requestLock(breakExisting = false) {
      socket.emit("requestLock", { lockUuid: state.lockUuid, force: breakExisting, resourceUuid: state.resourceUuid });
    },
    /**
     * Broadcasts the document update to all other clients
     */
    broadcastUpdate(document) {
      if (!state.lockUuid) {
        console.warn("Trying to broadcast document update without lock!");
        return;
      }
      socket.emit("contentUpdate", { resourceUuid: state.resourceUuid, lockUuid: state.lockUuid, document });
    },
    /**
     * Releases the lock on the resource
     * Sets the resourceUuid to empty string!
     */
    releaseLock() {
      if (!state.lockUuid) {
        console.warn("Trying to release lock without lock!");
        return;
      }
      socket.emit("releaseLock", state.resourceUuid);
      state.resourceUuid = "";
    },
    /**
     * Clears the lock notification
     */
    clearNotification() {
      if (state.lockNotification) {
        state.lockNotification();
      }
    },
    onDocumentUpdate(cb) {
      documentUpdateCallbacks.push(cb);
      return () => {
        documentUpdateCallbacks = documentUpdateCallbacks.filter((fn) => fn !== cb);
      };
    }
  });
  const { showNotification } = useNotificationStore();
  const i18n = getI18n();
  const clearNotification = () => {
    if (state.lockNotification) {
      state.lockNotification();
    }
  };
  const watchConfirmedHandler = (locks) => {
    const currentLock = locks[state.resourceUuid];
    if (!currentLock)
      return;
    state.lockInfo = currentLock;
  };
  const lockGrantedHandler = (lockInfo) => {
    if (lockInfo.resourceUuid !== state.resourceUuid)
      return;
    state.lockUuid = lockInfo.lockUuid;
    state.lockInfo = void 0;
  };
  const lockChangedHandler = (lockInfo) => {
    if (lockInfo.resourceUuid !== state.resourceUuid)
      return;
    state.lockInfo = lockInfo;
  };
  const contentUpdateHandler = async ({ document }) => {
    const resource = deserializeDatesInObject(document);
    for (const cb of documentUpdateCallbacks) {
      await cb(resource);
    }
  };
  const sessionLostHandler = () => {
    window.location.reload();
  };
  const connectHandler = () => {
    if (state.resourceUuid) {
      socket.emit("watch", [state.resourceUuid]);
    }
  };
  socket.on("watchConfirmed", watchConfirmedHandler);
  socket.on("lockGranted", lockGrantedHandler);
  socket.on("lockChanged", lockChangedHandler);
  socket.on("contentUpdate", contentUpdateHandler);
  socket.on("sessionLost", sessionLostHandler);
  socket.on("connect", connectHandler);
  const resourceWatcher = watch([isResourceChanged, () => !!state.resourceUuid], ([isResourceChanged2, hasResourceId]) => {
    if (isResourceChanged2 && hasResourceId) {
      state.requestLock();
    }
  });
  const lockWatcher = watch(
    () => state.lockInfo,
    async (lockInfo, oldLockInfo) => {
      clearNotification();
      if (!lockInfo || (lockInfo == null ? void 0 : lockInfo.resourceUuid) !== state.resourceUuid || (lockInfo == null ? void 0 : lockInfo.resourceType) !== resourceType)
        return;
      if (lockInfo.locked) {
        state.lockUuid = void 0;
        const message = lockInfo.isOwnLock ? i18n.t(translations.notificationLockedYourself) : i18n.t(translations.notificationLockedOthers) + " " + (lockInfo.ownerName || "<unknown>");
        state.lockNotification = showNotification(
          new CMSNotification(message, "info", {
            dismissible: false,
            timeout: null,
            callbackLabel: i18n.t(translations.notificationButtonBreakLock),
            callbackNoDismiss: true,
            callbackFunc: isBreakLockAllowed ? async () => {
              state.requestLock(true);
              await (lockChangeCallback == null ? void 0 : lockChangeCallback());
            } : void 0
          })
        );
      } else {
        if (oldLockInfo && oldLockInfo.locked) {
          showNotification(new CMSNotification(i18n.t(translations.notificationLockGranted), "positive"));
          await (lockChangeCallback == null ? void 0 : lockChangeCallback());
        }
      }
    },
    { deep: true }
  );
  state.clearInitializedResources = () => {
    socket.off("watchConfirmed", watchConfirmedHandler);
    socket.off("lockGranted", lockGrantedHandler);
    socket.off("lockChanged", lockChangedHandler);
    socket.off("contentUpdate", contentUpdateHandler);
    socket.off("sessionLost", sessionLostHandler);
    socket.off("connect", connectHandler);
    lockWatcher();
    resourceWatcher();
  };
  return state;
};
const useListLocking = (resourceType, list) => {
  const manager = useSockets();
  const socket = manager.socket(getResourceChannelName(resourceType));
  let documentUpdateCallbacks = [];
  const state = reactive({
    locks: {},
    async init() {
      socket.emit("watch", list.value);
    },
    onDocumentUpdate(cb) {
      documentUpdateCallbacks.push(cb);
      return () => {
        documentUpdateCallbacks = documentUpdateCallbacks.filter((fn) => fn !== cb);
      };
    },
    teardown() {
      documentUpdateCallbacks = [];
      socket.emit("unwatch", list.value);
    }
  });
  const watchConfirmedHandler = (locks) => {
    state.locks = locks;
  };
  const lockChangedHandler = (lock) => {
    state.locks[lock.resourceUuid] = lock;
  };
  const contentUpdateHandler = async ({ document }) => {
    const resource = deserializeDatesInObject(document);
    for (const cb of documentUpdateCallbacks) {
      await cb(resource);
    }
  };
  const sessionLostHandler = () => {
    window.location.reload();
  };
  const connectHandler = () => {
    if (list.value) {
      socket.emit("watch", list.value);
    }
  };
  socket.on("watchConfirmed", watchConfirmedHandler);
  socket.on("lockChanged", lockChangedHandler);
  socket.on("contentUpdate", contentUpdateHandler);
  socket.on("sessionLost", sessionLostHandler);
  socket.on("connect", connectHandler);
  const listWatcher = watch(list, (value, oldValue) => {
    if (oldValue && oldValue.length) {
      socket.emit("unwatch", oldValue);
      state.locks = {};
    }
    socket.emit("watch", value);
  });
  state.clearInitializedResources = () => {
    socket.off("watchConfirmed", watchConfirmedHandler);
    socket.off("lockChanged", lockChangedHandler);
    socket.off("contentUpdate", contentUpdateHandler);
    socket.off("sessionLost", sessionLostHandler);
    socket.off("connect", connectHandler);
    listWatcher();
  };
  return state;
};
const lockingModule = {
  messages,
  routes: []
};
export {
  getResourceChannelName,
  lockingModule,
  useListLocking,
  useSingleResourceLocking
};
//# sourceMappingURL=index.es.js.map
